import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useVault } from '../session/useVault';
import { pendingText } from '../vault/writeQueue';
import { baseWhileTyping, editableText, withText } from './textNote';

/** A note a caller just made: where, and the file exactly as written there. */
export type MintedNote = { path: string; raw: string };

/**
 * One text note, loaded and saved — the editor behind `/write`, taking a path.
 *
 * With no path there is no file yet, and [mint] is asked for one on the first
 * word rather than on arrival: opening an editor and changing your mind should
 * leave nothing behind. What the minting caller writes alongside the file — a
 * route's own URL, a nook's member line — is its business; this keeps typing
 * into the file it gets back, front matter and all.
 *
 * Saving is the write queue's job — edits coalesce per path and go out when
 * the typing stops.
 */
export function useNoteText(
  path: string | null,
  field: RefObject<HTMLTextAreaElement | null>,
  options: {
    mint?: (text: string) => MintedNote | null;
    /** Every change to the file, as it now stands. */
    onChange?: (path: string, raw: string) => void;
  } = {},
): {
  /** The file as it currently stands, which every write starts from — never
   *  the text on screen, which is only its readable half. Null while loading. */
  raw: string | null;
  text: string;
  missing: boolean;
  /** A file holding more than one message: there is no single run of prose to
   *  edit, and flattening it would destroy the structure the app built. */
  structured: boolean;
  edit: (next: string) => void;
} {
  const { state, write } = useVault();
  const { mint, onChange } = options;
  const [raw, setRaw] = useState<string | null>(path ? null : '');
  const [text, setText] = useState('');
  const [missing, setMissing] = useState(false);
  /** The path this editor minted, held here because the next keystroke can
   *  arrive before the caller hands it back — and minting twice would split
   *  one note across two files. */
  const minted = useRef<string | null>(null);
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!path) {
      minted.current = null;
      loadedFor.current = null;
      setRaw('');
      setText('');
      setMissing(false);
      return;
    }
    if (state.status !== 'ready') return;

    // Never over the cursor. This re-runs whenever the vault publishes, and
    // the vault publishes when it adopts a change made somewhere else — so a
    // note edited on the phone would otherwise replace what is being typed
    // here, mid-sentence. The file under it is still read: the next save has
    // to start from it (see baseWhileTyping). Only for the file already open:
    // a different one is always read whole.
    const switching = loadedFor.current !== path;
    const typing = !switching && document.activeElement === field.current;
    loadedFor.current = path;
    if (!typing) setMissing(false);

    // An edit made moments ago may not have reached the vault yet, so the
    // queue is asked first — reading past it would show a version older than
    // what was typed and then save that back over it.
    const queued = pendingText(path);
    if (queued !== undefined) {
      setRaw(queued);
      if (!typing) setText(editableText(queued) ?? '');
      return;
    }

    let cancelled = false;
    if (switching) setRaw(null);
    state.vault
      .readFile(path)
      .then((loaded) => {
        if (cancelled || (typing && pendingText(path) !== undefined)) return;
        if (loaded === null) {
          if (!typing) setMissing(true);
        } else if (typing) {
          setRaw((own) => (own === null ? loaded : baseWhileTyping(own, loaded)));
        } else {
          setRaw(loaded);
          setText(editableText(loaded) ?? '');
        }
      })
      .catch(() => !cancelled && !typing && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [state, path, field]);

  const edit = useCallback(
    (next: string) => {
      setText(next);
      if (state.status !== 'ready' || raw === null) return;

      const at = path || minted.current;
      if (at) {
        const change = (t: string) => withText(t, next);
        const updated = change(raw);
        setRaw(updated);
        write([{ path: at, text: updated, change }]);
        onChange?.(at, updated);
        return;
      }

      // The first word mints the file. A blank one is not a note yet.
      if (!next.trim() || !mint) return;
      const fresh = mint(next);
      if (!fresh) return;
      minted.current = fresh.path;
      loadedFor.current = fresh.path;
      setRaw(fresh.raw);
    },
    [state, raw, path, write, mint, onChange],
  );

  return {
    raw,
    text,
    missing,
    structured: path !== null && raw !== null && editableText(raw) === null,
    edit,
  };
}
